/**
 * NotificationBell Component
 * Shows notification bell with unread count and dropdown
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, Check, X, ExternalLink, Settings } from 'lucide-react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}
import { NotificationPreferences } from './NotificationPreferences';
import {
    getNotifications,
    getUnreadCount,
    markAsRead,
    markAllAsRead,
    subscribeToNotifications,
    Notification
} from '../../services/notificationService';
import { isErr } from '../../types/api/result';
import {
    getPendingInvitations,
    acceptInvitation,
    declineInvitation,
    BoardInvitation
} from '../../services/invitationService';
import { getCurrentUserId } from '../../services/authService';

interface NotificationBellProps {
    className?: string;
    onSelectBoard?: (boardId: string) => void;
}

export const NotificationBell: React.FC<NotificationBellProps> = ({ className = '', onSelectBoard }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [invitations, setInvitations] = useState<BoardInvitation[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [showPreferences, setShowPreferences] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Load notifications and invitations
    const loadData = useCallback(async () => {
        setIsLoading(true);
        try {
            const [notifResult, inviteResult, countResult] = await Promise.all([
                getNotifications(10),
                getPendingInvitations(),
                getUnreadCount()
            ]);

            if (notifResult.success) setNotifications(notifResult.data);
            if (inviteResult.success) setInvitations(inviteResult.data);
            if (countResult.success) setUnreadCount(countResult.data + (inviteResult.success ? inviteResult.data.length : 0));
        } catch (error) {
            console.error('Failed to load notifications', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Initial load
    useEffect(() => {
        loadData();
    }, [loadData]);

    // Real-time subscription
    useEffect(() => {
        const userId = getCurrentUserId();
        if (!userId) return;

        const unsubscribe = subscribeToNotifications(userId, (notification) => {
            setNotifications(prev => [notification, ...prev]);
            setUnreadCount(prev => prev + 1);
        });

        return unsubscribe;
    }, []);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    // Handle invitation accept
    const handleAccept = async (invitation: BoardInvitation) => {
        // Set local loading state if feasible, or just rely on optimistic UI for the list
        const previousInvitations = [...invitations];
        // Optimistic update - remove from list immediately to feel responsive
        setInvitations(prev => prev.filter(i => i.id !== invitation.id));
        setUnreadCount(prev => Math.max(0, prev - 1));

        try {
            // Call API and WAIT for success before navigating
            const result = await acceptInvitation(invitation.id);

            if (result.success) {
                // Navigate only after successful join
                if (onSelectBoard) {
                    onSelectBoard(invitation.boardId);
                } else {
                    // Use window.location only if no callback provided
                    window.location.href = `/?board=${invitation.boardId}`;
                }
                setIsOpen(false);
            } else if (isErr(result)) {
                throw new Error(result.error.message || 'Failed to accept');
            }
        } catch (error) {
            console.error('Accept invitation failed', error);
            // Restore on failure
            setInvitations(previousInvitations);
            setUnreadCount(prev => prev + 1);
            // Optionally show toast/alert here
        }
    };

    // Handle invitation decline
    const handleDecline = async (invitation: BoardInvitation) => {
        // Optimistic update - remove immediately
        const previousInvitations = [...invitations];
        setInvitations(prev => prev.filter(i => i.id !== invitation.id));
        setUnreadCount(prev => Math.max(0, prev - 1));

        // Call API in background
        const result = await declineInvitation(invitation.id);
        if (!result.success) {
            // Restore on failure
            setInvitations(previousInvitations);
            setUnreadCount(prev => prev + 1);
        }
    };

    // Handle notification click
    const handleNotificationClick = async (notification: Notification) => {
        if (!notification.isRead) {
            await markAsRead(notification.id);
            setNotifications(prev => prev.map(n =>
                n.id === notification.id ? { ...n, isRead: true } : n
            ));
            setUnreadCount(prev => Math.max(0, prev - 1));
        }

        // Navigate based on notification type
        if (notification.type === 'board_invitation' && notification.data.board_id) {
            const boardId = notification.data.board_id as string;
            if (onSelectBoard) {
                onSelectBoard(boardId);
            } else {
                window.location.href = `/?board=${boardId}`;
            }
            setIsOpen(false);
        }
    };

    // Mark all as read
    const handleMarkAllRead = async () => {
        await markAllAsRead();
        setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
        setUnreadCount(invitations.length); // Keep invitation count
    };

    // Format relative time
    const formatTime = (dateStr: string): string => {
        const date = new Date(dateStr);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString();
    };

    const totalCount = unreadCount;

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            {/* Bell Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="relative p-2 rounded-lg hover:bg-white/10 transition-colors"
                aria-label="Notifications"
            >
                <Bell className="w-5 h-5 text-gray-400 hover:text-white transition-colors" />

                {/* Badge */}
                <AnimatePresence>
                    {totalCount > 0 && (
                        <motion.span
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0 }}
                            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full"
                        >
                            {totalCount > 99 ? '99+' : totalCount}
                        </motion.span>
                    )}
                </AnimatePresence>
            </button>

            {/* Dropdown */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        transition={{ duration: 0.15 }}
                        className="absolute right-0 top-full mt-2 w-80 bg-[#1a1a1a] border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                            <h3 className="font-semibold text-white">Notifications</h3>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setShowPreferences(!showPreferences)}
                                    className={cn(
                                        "p-1.5 rounded-lg transition-colors",
                                        showPreferences ? "bg-blue-500/20 text-blue-400" : "text-gray-400 hover:bg-white/5"
                                    )}
                                    title="Notification Preferences"
                                >
                                    <Settings className="w-4 h-4" />
                                </button>
                                {notifications.some(n => !n.isRead) && !showPreferences && (
                                    <button
                                        onClick={handleMarkAllRead}
                                        className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                                    >
                                        Mark all read
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Content */}
                        <div className="max-h-[500px] overflow-y-auto thin-scrollbar">
                            {showPreferences ? (
                                <div className="p-2">
                                    <NotificationPreferences />
                                </div>
                            ) : isLoading ? (
                                <div className="p-8 text-center text-gray-500">
                                    <div className="animate-spin w-6 h-6 border-2 border-white/20 border-t-white/60 rounded-full mx-auto" />
                                </div>
                            ) : invitations.length === 0 && notifications.length === 0 ? (
                                <div className="p-8 text-center text-gray-500">
                                    <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                    <p className="text-sm">No notifications</p>
                                </div>
                            ) : (
                                <>
                                    {/* Pending Invitations */}
                                    {invitations.map(invitation => (
                                        <div
                                            key={invitation.id}
                                            className="p-4 border-b border-white/5 bg-blue-500/5"
                                        >
                                            <div className="flex items-start gap-3">
                                                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                                                    <ExternalLink className="w-5 h-5 text-blue-400" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm text-white font-medium">
                                                        Board Invitation
                                                    </p>
                                                    <p className="text-xs text-gray-400 mt-0.5 truncate">
                                                        {invitation.inviterName || 'Someone'} invited you to "{invitation.boardTitle}"
                                                    </p>
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        {formatTime(invitation.createdAt)} · {invitation.role}
                                                    </p>

                                                    {/* Action Buttons */}
                                                    <div className="flex gap-2 mt-3">
                                                        <button
                                                            onClick={() => handleAccept(invitation)}
                                                            className="flex-1 px-3 py-1.5 text-xs font-medium bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors flex items-center justify-center gap-1"
                                                        >
                                                            <Check className="w-3.5 h-3.5" />
                                                            Accept
                                                        </button>
                                                        <button
                                                            onClick={() => handleDecline(invitation)}
                                                            className="flex-1 px-3 py-1.5 text-xs font-medium bg-white/10 hover:bg-white/20 text-gray-300 rounded-lg transition-colors flex items-center justify-center gap-1"
                                                        >
                                                            <X className="w-3.5 h-3.5" />
                                                            Decline
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                    {/* Regular Notifications */}
                                    {notifications.map(notification => (
                                        <div
                                            key={notification.id}
                                            onClick={() => handleNotificationClick(notification)}
                                            className={`p-4 border-b border-white/5 cursor-pointer hover:bg-white/5 transition-colors ${!notification.isRead ? 'bg-white/[0.02]' : ''
                                                }`}
                                        >
                                            <div className="flex items-start gap-3">
                                                <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${notification.isRead ? 'bg-transparent' : 'bg-blue-500'
                                                    }`} />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm text-white font-medium">
                                                        {notification.title}
                                                    </p>
                                                    {notification.message && (
                                                        <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">
                                                            {notification.message}
                                                        </p>
                                                    )}
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        {formatTime(notification.createdAt)}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
